import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { format } from 'date-fns';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { Report, Page } from 'cccisd-laravel-report';
import Table from 'cccisd-table';
import query from './climate.graphql';
import style from './style.css';

/* Data structure:

{
    climate_safe: {
        numResponses: 0,
        totalScore: 0,
        questionText: 'I feel safe at school.',

        "1": 0,
        "2": 0,
        "3": 0,
        "4": 0,

        byGrade: {
            "gradek": {
                numResponses: 0,
                totalScore: 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
            },
            "grade1": {
                numResponses: 0,
                totalScore: 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
            },
            ...
        }
    },

    climate_fair: { ... }
}

*/
const initialScoreMap = {
    climate_safe: {
        questionText: 'I feel safe at school.',
    },
    climate_fair: {
        questionText: 'The rules in my school are fair.',
    },
    climate_teachers_care: {
        questionText: 'My teachers care about me.',
    },
    climate_kids_care: {
        questionText: 'Kids in my school care about me.',
    },
    climate_teacher_helps: {
        questionText: 'My teachers help me with schoolwork when I need it.',
    },
    climate_adults_encourage: {
        questionText: 'Adults in my school encourage me to work hard.',
    },

    ms_climate_adult_trust: {
        questionText: 'At school, there is at least one adult I trust.',
    },
    ms_climate_know_who_for_help: {
        questionText: 'At school, I know who to go to if I need help.',
    },
    ms_climate_one_good_friend: {
        questionText: 'At school, I have at least one good friend.',
    },
    ms_climate_rules: {
        questionText: 'Rules help make school a better place.',
    },
    ms_climate_teachers_help: {
        questionText: "My teachers help me when I don't understand something.",
    },
    ms_climate_teachers_challenge: {
        questionText: 'My teachers challenge me to learn new things.',
    },
};

const answerOptions = {
    1: {
        text: 'Never',
        color: 'rgb(190, 30, 30)',
    },
    2: {
        text: 'Sometimes',
        color: 'rgb(246, 161, 1)',
    },
    3: {
        text: 'A lot',
        color: 'rgb(3, 170, 180)',
    },
    4: {
        text: 'Always',
        color: 'rgb(30, 190, 30)',
    },
};

let min;
let max;
Object.keys(answerOptions).forEach(scoreKey => {
    const val = parseInt(scoreKey, 10);
    if (!min || val < min) {
        min = val;
    }

    if (!max || val > max) {
        max = val;
    }
});

const step = (max - min) / Object.keys(answerOptions).length;

const ClimateReport = ({ currTimepoint, isMs }) => {
    const surveyHandle = isMs ? 'climate_ms' : 'climate';
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [schools, setSchools] = useState([]);
    const [classes, setClasses] = useState([]);

    useEffect(() => {
        async function getData() {
            setIsLoading(true);
            const resp = await client.query({
                query,
                variables: { timepoint: '' + currTimepoint, assignmentId: isMs ? 3 : 1 },
                fetchPolicy: 'network-only',
            });

            const schoolResponseRateMap = {};
            const classResponseRateMap = {};

            // JSON does a deep copy, so it's not editing the original in memory
            const allScores = JSON.parse(JSON.stringify(initialScoreMap));
            Object.keys(allScores).forEach(devTag => {
                allScores[devTag].numResponses = 0;
                allScores[devTag].totalScore = 0;
                allScores[devTag].byGrade = {};
                Object.keys(answerOptions).forEach(opt => {
                    allScores[devTag][opt] = 0;
                });
            });

            resp.data.roles.learnerList.forEach(learner => {
                const isComplete =
                    _get(learner, 'assignmentProgress.completed', false) &&
                    _get(learner, 'assignmentProgress.surveyProgressStatus.' + surveyHandle) > 1;
                const schoolName = _get(learner, 'ancestorGroups.site.group.label', '');
                const schoolId = _get(learner, 'ancestorGroups.site.group.groupId', '');
                const classroomName = _get(learner, 'ancestorGroups.class.group.label', '');
                const classroomId = _get(learner, 'ancestorGroups.class.group.groupId', '');

                let grade = _get(learner, 'fields.grade', 'unknown') || 'unknown';
                if (grade === 0 || grade === '0') {
                    grade = 'k';
                }

                let isClimateExpected = false;
                try {
                    const selectedSurveyHandles =
                        _get(learner, 'selectedAssignmentPlan.session.settings.options', []) || [];
                    isClimateExpected = selectedSurveyHandles.includes(surveyHandle);
                } catch (e) {
                    // meh *shrug*
                    isClimateExpected = false;
                }

                if (!(schoolId in schoolResponseRateMap)) {
                    schoolResponseRateMap[schoolId] = { id: schoolId, school: schoolName, total: 0, complete: 0 };
                }
                if (!(classroomId in classResponseRateMap)) {
                    classResponseRateMap[classroomId] = {
                        id: classroomId,
                        classroom: classroomName,
                        school: schoolName,
                        total: 0,
                        complete: 0,
                    };
                }

                schoolResponseRateMap[schoolId].total++;
                classResponseRateMap[classroomId].total++;
                if (isComplete && isClimateExpected) {
                    schoolResponseRateMap[schoolId].complete++;
                    classResponseRateMap[classroomId].complete++;
                }

                const byGradeKey = `grade${grade}`;
                Object.keys(allScores).forEach(devTag => {
                    if (!(byGradeKey in allScores[devTag].byGrade)) {
                        allScores[devTag].byGrade[byGradeKey] = {
                            numResponses: 0,
                            totalScore: 0,
                            ...Object.keys(answerOptions).reduce((all, opt) => {
                                all[opt] = 0;
                                return all;
                            }, {}),
                        };
                    }

                    let score = _get(learner, `assignmentProgress.devTags.${devTag}`, '');
                    if (!score && score !== 0) {
                        return;
                    }

                    score = parseInt(score, 10);
                    allScores[devTag].numResponses++;
                    allScores[devTag].totalScore += score;
                    allScores[devTag].byGrade[byGradeKey].numResponses++;
                    allScores[devTag].byGrade[byGradeKey].totalScore += score;

                    // keep count of how many 1's, how many 2's, etc...
                    if (!(score in allScores[devTag])) {
                        console.error(`Unexpected score value: ${score} for devTag ${devTag}`);
                        allScores[devTag][score] = 0;
                    }

                    if (!(score in allScores[devTag].byGrade[byGradeKey])) {
                        console.error(`Unexpected score value: ${score} for devTag ${devTag}`);
                        allScores[devTag].byGrade[byGradeKey][score] = 0;
                    }

                    allScores[devTag][score]++;
                    allScores[devTag].byGrade[byGradeKey][score]++;
                });
            });

            // Only show either Elem or Middle, but not both
            let filteredAllScores = {};
            Object.keys(allScores)
                .filter(k => k.startsWith(isMs ? 'ms_climate_' : 'climate_'))
                .forEach(k => {
                    filteredAllScores[k] = allScores[k];
                });

            setData(filteredAllScores);
            setSchools(schoolResponseRateMap);
            setClasses(classResponseRateMap);
            setIsLoading(false);
        }

        getData();
    }, [currTimepoint]);

    function sortAnswerOptions(a, b) {
        return b.localeCompare(a); // will sort 4,3,2,1
    }

    function getRowColorFromMean(mean) {
        mean = parseFloat(mean);
        if (Number.isNaN(mean)) {
            return;
        }

        if (Object.keys(answerOptions).length <= 0) {
            return;
        }

        if (mean < min || mean > max) {
            return;
        }

        let rowColor = '';
        for (let i = 0; i < Object.keys(answerOptions).length; i++) {
            const compareVal = min + step * (i + 1);
            if (mean <= compareVal) {
                rowColor = answerOptions[Object.keys(answerOptions)[i]].color;
                break;
            }
        }

        return rowColor;
    }

    function renderPageHeading(heading) {
        return <h1 className="mb-5 pb-2 border-bottom border-primary border-4">{heading}</h1>;
    }

    function renderCoverPage() {
        return (
            <Page>
                <div>
                    {renderPageHeading((isMs ? 'Middle School' : 'Elementary') + ' Climate Assessment')}
                    <p>
                        This report is a compilation of responses for the{' '}
                        <b>{isMs ? 'Middle School ' : 'Elementary '} Climate Assessment</b>. The response rate is not
                        expected to be 100% for all Schools and Classes. For Schools or Classes where the response rate
                        is 0%, it is possible that those students were assigned the{' '}
                        {isMs ? 'Elementary ' : 'Middle School '}
                        Climate Assessment, instead, or they were not assigned a climate survey at all.
                    </p>
                </div>
            </Page>
        );
    }

    function renderParticipation({ isSchool }) {
        const label = isSchool ? 'School' : 'Class';
        const heading = renderPageHeading(`${label} Participation`);

        if (!Array.isArray(Object.values(classes)) || Object.values(classes).length <= 0) {
            return (
                <Page>
                    {heading}
                    <div className="alert alert-info">
                        <p className="text-info-emphasis mb-0">No {label} participation data to show.</p>
                    </div>
                </Page>
            );
        }

        return (
            <Page>
                {heading}
                <p className="mb-4">
                    Note: Some students may not be assigned {isMs ? 'Middle School' : 'Elementary'} Climate surveys. In
                    this case, Students who complete their assessment will not be counted towards the Climate Response
                    rate.
                </p>
                <div id={isSchool ? 'school_participation' : 'class_participation'}>
                    <Table
                        name={`${label}_participation`}
                        wrapperClassName={style.participationTable}
                        showPerPageOptions={false}
                        perPage={99999}
                        columns={[
                            { name: 'school', label: 'School' },
                            ...(!isSchool ? [{ name: 'classroom', label: 'Class' }] : []),
                            {
                                name: 'total',
                                label: 'Students',
                                class: 'text-center',
                            },
                            {
                                name: 'complete',
                                label: 'Climate Responses',
                                class: 'text-center',
                            },
                            {
                                name: 'id',
                                label: 'Climate Response %',
                                class: 'text-center',
                                render: (value, row) => {
                                    let pcnt = Math.round((row.complete / row.total) * 100);
                                    if (Number.isNaN(pcnt)) {
                                        pcnt = 0;
                                    }
                                    return <span>{pcnt}%</span>;
                                },
                            },
                        ]}
                        data={Object.values(isSchool ? schools : classes).sort((a, b) => {
                            if (isSchool) {
                                return a.school.localeCompare(b.school);
                            }

                            const schoolCompare = a.school.localeCompare(b.school);
                            if (schoolCompare === 0) {
                                return a.classroom.localeCompare(b.classroom);
                            }

                            return schoolCompare;
                        })}
                        rowKey="id"
                        saveState={false}
                        hideShowingRowsSummary
                    />
                </div>
            </Page>
        );
    }

    function renderScoreKeyTableHeaders() {
        return Object.keys(answerOptions)
            .sort(sortAnswerOptions)
            .map((scoreKey, i) => {
                const opt = answerOptions[scoreKey];
                return (
                    <th key={scoreKey + i} className={style.colNumeric}>
                        <div>{opt.text}</div>
                        <div className={style.scoreKey}>(Score={scoreKey})</div>
                        <div className={style.colorKey} style={{ backgroundColor: opt.color }} />
                    </th>
                );
            });
    }

    function renderSummary() {
        return (
            <Page>
                {renderPageHeading('Summary')}
                <table className={style.summaryTable} id="summary_all">
                    <thead>
                        <tr>
                            <th className={style.col1} />
                            {renderScoreKeyTableHeaders()}
                            <th className={style.colGraph}>Distribution</th>
                            <th className={style.colNumeric}>Response total</th>
                            <th className={style.colNumeric}>Average score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(data).map(devTag => {
                            let mean = (data[devTag].totalScore / data[devTag].numResponses).toFixed(2);
                            if (mean !== 0 && (!mean || Number.isNaN(mean) || mean === 'NaN')) {
                                mean = '-';
                            }

                            const rowColor = getRowColorFromMean(mean);

                            const dataPerResponseValue = Object.keys(data[devTag])
                                .filter(key => !Number.isNaN(parseInt(key, 10)))
                                .reduce((all, scoreKey) => {
                                    let responses = data[devTag][scoreKey];
                                    if (Number.isNaN(responses)) {
                                        responses = 0;
                                    }

                                    let percent = Math.round((responses / data[devTag].numResponses) * 100);
                                    if (Number.isNaN(percent)) {
                                        percent = 0;
                                    }

                                    all[scoreKey] = { responses, percent };
                                    return all;
                                }, {});

                            return (
                                <tr key={devTag}>
                                    <td className={style.col1}>{data[devTag].questionText}</td>
                                    {Object.keys(dataPerResponseValue)
                                        .sort(sortAnswerOptions)
                                        .map(scoreKey => {
                                            return (
                                                <td key={scoreKey} className={style.colNumeric}>
                                                    {dataPerResponseValue[scoreKey].percent}%
                                                    <br />({dataPerResponseValue[scoreKey].responses})
                                                </td>
                                            );
                                        })}
                                    <td className={style.colGraph}>
                                        <div className={style.graphContainer}>
                                            {Object.keys(dataPerResponseValue)
                                                .sort(sortAnswerOptions)
                                                .map(scoreKey => {
                                                    const stylez = {
                                                        backgroundColor:
                                                            scoreKey in answerOptions
                                                                ? answerOptions[scoreKey].color
                                                                : 'gray',
                                                        width: `${dataPerResponseValue[scoreKey].percent || 1}%`,
                                                        height:
                                                            Math.floor(
                                                                (1 / Object.keys(dataPerResponseValue).length) * 100
                                                            ) + '%',
                                                    };

                                                    return <div style={stylez} key={devTag + scoreKey} />;
                                                })}
                                        </div>
                                    </td>
                                    <td className={`${style.colNumeric} ${style.louderText}`}>
                                        {data[devTag].numResponses}
                                    </td>
                                    <td className={`${style.colNumeric} ${style.louderText}`}>
                                        <span className={style.mean}>{mean}</span>
                                        {rowColor && (
                                            <span
                                                className={style.colorIndicator}
                                                style={{ backgroundColor: rowColor }}
                                            />
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Page>
        );
    }

    function renderPerDevTagPerGrade(devTag, i) {
        const heading = (
            <div>
                {renderPageHeading('Item Summary')}
                <p className="mb-5 border-bottom border-primary-subtle pb-2">
                    <span className="me-2 fs-6 fw-bold">Question:</span>
                    <span className="fs-3 fw-lighter fst-italic">
                        &quot;{initialScoreMap[devTag].questionText}&quot;
                    </span>
                </p>
            </div>
        );

        const perDevTagData = _get(data, `${devTag}.byGrade`);
        if (!perDevTagData || Object.keys(perDevTagData).length <= 0) {
            return (
                <Page>
                    {heading}
                    <div className="alert alert-info">
                        <p className="text-info-emphasis mb-0">No Climate data to show for this question.</p>
                    </div>
                </Page>
            );
        }

        return (
            <Page key={devTag}>
                <div className="summary_per_grade">
                    {heading}
                    <table className={style.summaryTable}>
                        <thead>
                            <tr>
                                <th className={style.col1} />
                                {renderScoreKeyTableHeaders()}
                                <th className={style.colGraph}>Distribution</th>
                                <th className={style.colNumeric}>Response total</th>
                                <th className={style.colNumeric}>Average score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(perDevTagData)
                                .sort((a, b) => {
                                    if (a === 'gradek') {
                                        return -1;
                                    }
                                    if (b === 'gradek') {
                                        return 1;
                                    }

                                    return a.localeCompare(b);
                                })
                                .map(gradeKey => {
                                    const gradeLabel = 'Grade ' + gradeKey.replace('grade', '').toUpperCase();
                                    const rowData = perDevTagData[gradeKey];
                                    let responses =
                                        'numResponses' in rowData && !Number.isNaN(rowData.numResponses)
                                            ? rowData.numResponses
                                            : 0;
                                    let totalScore =
                                        'totalScore' in rowData && !Number.isNaN(rowData.totalScore)
                                            ? rowData.totalScore
                                            : 0;
                                    let mean = (totalScore / responses).toFixed(2);
                                    if (Number.isNaN(mean) || (!mean && mean !== 0) || mean === 'NaN') {
                                        mean = '-';
                                    }

                                    let rowColor = getRowColorFromMean(mean);

                                    return (
                                        <tr key={gradeLabel}>
                                            <td className={style.col1}>{gradeLabel}</td>
                                            {Object.keys(answerOptions)
                                                .sort(sortAnswerOptions)
                                                .map(scoreKey => {
                                                    let percent = Math.round(
                                                        (rowData[scoreKey] / rowData.numResponses) * 100
                                                    );
                                                    if (!percent || Number.isNaN(percent)) {
                                                        percent = 0;
                                                    }

                                                    const perScoreResponses = rowData[scoreKey] || 0;
                                                    return (
                                                        <td key={scoreKey} className={style.colNumeric}>
                                                            {percent}%
                                                            <br />({perScoreResponses})
                                                        </td>
                                                    );
                                                })}
                                            <td className={style.colGraph}>
                                                <div className={style.graphContainer}>
                                                    {Object.keys(answerOptions)
                                                        .sort(sortAnswerOptions)
                                                        .map(scoreKey => {
                                                            let percent = Math.round(
                                                                (rowData[scoreKey] / rowData.numResponses) * 100
                                                            );
                                                            if (!percent || Number.isNaN(percent)) {
                                                                percent = 0;
                                                            }

                                                            const stylez = {
                                                                backgroundColor:
                                                                    scoreKey in answerOptions
                                                                        ? answerOptions[scoreKey].color
                                                                        : 'gray',
                                                                width: `${percent || 1}%`,
                                                                height:
                                                                    Math.floor(
                                                                        (1 / (Object.keys(rowData).length - 2)) * 100
                                                                    ) + '%',
                                                            };

                                                            return <div style={stylez} key={scoreKey} />;
                                                        })}
                                                </div>
                                            </td>
                                            <td className={`${style.colNumeric} ${style.louderText}`}>{responses}</td>
                                            <td className={`${style.colNumeric} ${style.louderText}`}>
                                                <span className={style.mean}>{mean}</span>
                                                {rowColor && (
                                                    <span
                                                        className={style.colorIndicator}
                                                        style={{ backgroundColor: rowColor }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </Page>
        );
    }

    if (isLoading || !data) {
        return <Loader loading />;
    }

    const now = new Date();
    return (
        <Report downloadFilename={'Climate_Report_' + format(now, 'yyyy-MM-dd')}>
            {renderCoverPage()}
            {renderParticipation({ isSchool: true })}
            {renderParticipation({ isSchool: false })}
            {renderSummary()}
            {Object.keys(data).map(renderPerDevTagPerGrade)}
        </Report>
    );
};

ClimateReport.propTypes = {
    currTimepoint: PropTypes.string,
    isMs: PropTypes.bool,
};

export default ClimateReport;
